import type {
  BackgroundAdjustments,
  DiffuseAdjustments,
  LoadedMockupAssets,
  ReflectionAdjustments
} from './composite';
import { renderMaster } from './composite';
import { createCanvas, loadVideoFromUrl, seekVideo } from './loadImage';
import type {
  CustomPlacement,
  MockupTemplate,
  MotionAtlasContract,
  MotionAtlasLayout,
  MotionResizeMode,
  Point,
  ScreenWarpMode
} from '../types';
import { resolveMotionAtlasAuxRole } from '@motionpitch/template-contract';

type MotionAtlasVariant = 'preview' | 'export';

export interface MotionAtlasSpec {
  columns: number;
  rows: number;
  tileWidth: number;
  tileHeight: number;
  atlasWidth: number;
  atlasHeight: number;
}

export interface MotionTrackingFrame {
  index: number;
  time: number;
  screenQuad: Point[];
}

export interface MotionTrackingSequence {
  fps: number;
  frameCount: number;
  duration: number;
  outputWidth: number;
  outputHeight: number;
  frames: MotionTrackingFrame[];
  frameMap: Map<number, MotionTrackingFrame>;
  bounds: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  };
  staticQuad: boolean;
}

export interface LoadedMotionAssets {
  template: MockupTemplate;
  variant: MotionAtlasVariant;
  atlasVideo: HTMLVideoElement;
  tracking: MotionTrackingSequence;
  atlasSpec: MotionAtlasSpec;
}

export interface MotionRenderOptions {
  renderSize?: number;
  screenRenderScale?: number;
  gridX?: number;
  gridY?: number;
  diffuse?: DiffuseAdjustments;
  background?: BackgroundAdjustments;
  reflection?: ReflectionAdjustments;
  useAlternateMask?: boolean;
  customPlacement?: CustomPlacement | null;
  warpMode?: ScreenWarpMode;
  realtime?: boolean;
  atlasSourceOverride?: CanvasImageSource;
  trackingQuadOverride?: Point[];
}

export interface MotionResizeDimensions {
  outputWidth: number;
  outputHeight: number;
  sourceCropX: number;
  sourceCropY: number;
  sourceCropWidth: number;
  sourceCropHeight: number;
}

const DEFAULT_OUTPUT_SIZE = 1920;
const DEFAULT_FPS = 30;
const MOTION_WIDESCREEN_ASPECT = 16 / 9;
const MOTION_VERTICAL_ASPECT = 9 / 16;
const TRACKING_FRAME_OFFSET = 0;
// Export uses interpolateTrackingQuadAtTime (motionExport.ts -> trackingQuadOverride).
// Keep export independent so preview/export offsets can be tuned separately if needed.
const TRACKING_FRAME_EXPORT_OFFSET = 0;

export async function loadMotionAssets(
  template: MockupTemplate,
  variant: MotionAtlasVariant
): Promise<LoadedMotionAssets> {
  const atlasUrl = variant === 'preview' ? template.previewAtlasUrl : template.atlasUrl;
  const trackingUrl = template.trackingUrl;

  if (!atlasUrl) {
    throw new Error(`Motion ${variant} atlas is missing for this template.`);
  }
  if (!trackingUrl) {
    throw new Error('Motion tracking JSON is missing for this template.');
  }

  const [atlasVideo, tracking] = await Promise.all([loadVideoFromUrl(atlasUrl), loadMotionTracking(trackingUrl, template)]);
  const atlasSpec = getMotionAtlasSpec(template.atlasLayout || 'standard-4', variant);

  if (atlasVideo.videoWidth !== atlasSpec.atlasWidth || atlasVideo.videoHeight !== atlasSpec.atlasHeight) {
    throw new Error(
      `Motion atlas has invalid size: expected ${atlasSpec.atlasWidth}x${atlasSpec.atlasHeight}, got ${atlasVideo.videoWidth}x${atlasVideo.videoHeight}.`
    );
  }

  atlasVideo.pause();
  atlasVideo.muted = true;
  atlasVideo.playsInline = true;

  return {
    template,
    variant,
    atlasVideo,
    tracking,
    atlasSpec
  };
}

export function getMotionAtlasSpec(layout: MotionAtlasLayout, variant: MotionAtlasVariant): MotionAtlasSpec {
  const tileSize = variant === 'preview' ? 640 : 1920;
  if (layout === 'extended-6') {
    return {
      columns: 3,
      rows: 2,
      tileWidth: tileSize,
      tileHeight: tileSize,
      atlasWidth: tileSize * 3,
      atlasHeight: tileSize * 2
    };
  }

  return {
    columns: 2,
    rows: 2,
    tileWidth: tileSize,
    tileHeight: tileSize,
    atlasWidth: tileSize * 2,
    atlasHeight: tileSize * 2
  };
}

export function resolveMotionAtlasContract(
  template: Pick<MockupTemplate, 'atlasContract' | 'atlasLayout'>
): MotionAtlasContract {
  if (template.atlasContract === 'screen-overlay-v1' || template.atlasContract === 'legacy-reflection-v1') {
    return template.atlasContract;
  }

  return template.atlasLayout === 'extended-6' ? 'legacy-reflection-v1' : 'screen-overlay-v1';
}

export function getMotionFrameIndex(sequence: MotionTrackingSequence, currentTime: number): number {
  const rawIndex = Math.floor(Math.max(0, currentTime) * sequence.fps);
  return Math.max(0, Math.min(sequence.frameCount - 1, rawIndex));
}

export function getMotionFrameTime(sequence: MotionTrackingSequence, frameIndex: number): number {
  return Math.max(0, Math.min(frameIndex, sequence.frameCount - 1)) / Math.max(1, sequence.fps);
}

export function getMotionAtlasFrameTime(sequence: MotionTrackingSequence, frameIndex: number): number {
  const fps = Math.max(1, sequence.fps);
  const clampedIndex = Math.max(0, Math.min(frameIndex, sequence.frameCount - 1));
  const frameTime = clampedIndex / fps;
  const insideFrameOffset = 0.25 / fps;
  return clampMotionSequenceTime(sequence, frameTime + insideFrameOffset);
}

export function getMotionSequenceDuration(sequence: MotionTrackingSequence): number {
  const explicitDuration = Number.isFinite(sequence.duration) ? Math.max(0, sequence.duration) : 0;
  const frameDuration = Math.max(0, sequence.frameCount || sequence.frames.length || 1) / Math.max(1, sequence.fps);
  return Math.max(explicitDuration, frameDuration);
}

export function getMotionSourceDuration(userSource: CanvasImageSource | null): number | null {
  if (!(userSource instanceof HTMLVideoElement)) {
    return null;
  }
  const duration = userSource.duration;
  if (!Number.isFinite(duration) || duration <= 0) {
    return null;
  }
  return duration;
}

export function getEffectiveMotionPlaybackDuration(
  sequence: MotionTrackingSequence,
  userSource: CanvasImageSource | null
): number {
  return Math.max(getMotionSequenceDuration(sequence), getMotionSourceDuration(userSource) ?? 0);
}

export function clampMotionSequenceTime(sequence: MotionTrackingSequence, time: number): number {
  return Math.max(0, Math.min(time, getMotionSequenceDuration(sequence)));
}

export async function seekMotionVideos(
  atlasVideo: HTMLVideoElement | null,
  userSource: CanvasImageSource | null,
  time: number,
  signal?: AbortSignal
): Promise<void> {
  await Promise.all([
    atlasVideo ? seekVideo(atlasVideo, time, { signal }) : Promise.resolve(),
    userSource instanceof HTMLVideoElement ? seekVideo(userSource, time, { signal }) : Promise.resolve()
  ]);
}

export function renderFrame(
  motionAssets: LoadedMotionAssets,
  userSource: CanvasImageSource | null,
  frameIndex: number,
  options: MotionRenderOptions = {}
): HTMLCanvasElement {
  const frame = resolveTrackingFrame(motionAssets.tracking, frameIndex);
  const trackingQuad = options.trackingQuadOverride && options.trackingQuadOverride.length === 4
    ? options.trackingQuadOverride
    : frame.screenQuad;
  const atlasFrameAssets = extractAtlasFrameAssets(
    motionAssets.template,
    options.atlasSourceOverride || motionAssets.atlasVideo,
    motionAssets.atlasSpec,
    motionAssets.template.atlasLayout || 'standard-4'
  );
  const gridDensity = options.realtime ? 14 : 42;
  const reflection = options.realtime
    ? reduceRealtimeReflection(options.reflection)
    : options.reflection;

  return renderMaster(atlasFrameAssets, userSource, {
    renderSize: options.renderSize || motionAssets.template.outputSize || DEFAULT_OUTPUT_SIZE,
    screenRenderScale: options.screenRenderScale,
    gridX: options.gridX ?? gridDensity,
    gridY: options.gridY ?? gridDensity,
    diffuse: options.diffuse,
    background: options.background,
    reflection,
    useAlternateMask: options.useAlternateMask,
    customPlacement: options.customPlacement,
    warpMode: options.warpMode,
    realtime: options.realtime,
    screenQuadOverride: scaleTrackingQuad(
      trackingQuad,
      motionAssets.tracking,
      motionAssets.template.outputSize || DEFAULT_OUTPUT_SIZE
    )
  });
}

export function interpolateTrackingQuadAtTime(sequence: MotionTrackingSequence, time: number): Point[] {
  const maxFrameIndex = Math.max(0, sequence.frameCount - 1);
  const rawFrame = Math.max(
    0,
    Math.min(time * Math.max(1, sequence.fps) + TRACKING_FRAME_EXPORT_OFFSET, maxFrameIndex)
  );
  const lowerIndex = Math.floor(rawFrame);
  const upperIndex = Math.ceil(rawFrame);

  const lowerFrame = resolveTrackingFrame(sequence, lowerIndex);
  if (upperIndex === lowerIndex) {
    return lowerFrame.screenQuad.map((point) => ({ ...point }));
  }

  const upperFrame = resolveTrackingFrame(sequence, upperIndex);
  const t = rawFrame - lowerIndex;

  return lowerFrame.screenQuad.map((point, index) => {
    const nextPoint = upperFrame.screenQuad[index] || point;
    return {
      x: point.x + (nextPoint.x - point.x) * t,
      y: point.y + (nextPoint.y - point.y) * t
    };
  });
}

export function getMotionResizeDimensions(
  resizeMode: MotionResizeMode,
  squareRenderSize: number
): MotionResizeDimensions {
  const safeSquareSize = Math.max(1, Math.round(squareRenderSize));
  if (resizeMode === '16:9') {
    const cropHeight = Math.max(1, Math.round(safeSquareSize / MOTION_WIDESCREEN_ASPECT));
    const cropY = Math.max(0, Math.round((safeSquareSize - cropHeight) / 2));
    return {
      outputWidth: safeSquareSize,
      outputHeight: cropHeight,
      sourceCropX: 0,
      sourceCropY: cropY,
      sourceCropWidth: safeSquareSize,
      sourceCropHeight: cropHeight
    };
  }

  if (resizeMode === '9:16') {
    const cropWidth = Math.max(1, Math.round(safeSquareSize * MOTION_VERTICAL_ASPECT));
    const cropX = Math.max(0, Math.round((safeSquareSize - cropWidth) / 2));
    return {
      outputWidth: cropWidth,
      outputHeight: safeSquareSize,
      sourceCropX: cropX,
      sourceCropY: 0,
      sourceCropWidth: cropWidth,
      sourceCropHeight: safeSquareSize
    };
  }

  return {
    outputWidth: safeSquareSize,
    outputHeight: safeSquareSize,
    sourceCropX: 0,
    sourceCropY: 0,
    sourceCropWidth: safeSquareSize,
    sourceCropHeight: safeSquareSize
  };
}

export function createMotionViewportCanvas(
  source: CanvasImageSource,
  resizeMode: MotionResizeMode,
  options: {
    squareRenderSize: number;
    targetWidth?: number;
    targetHeight?: number;
  }
): HTMLCanvasElement {
  const viewport = getMotionResizeDimensions(resizeMode, options.squareRenderSize);
  const targetWidth = Math.max(1, Math.round(options.targetWidth ?? viewport.outputWidth));
  const targetHeight = Math.max(1, Math.round(options.targetHeight ?? viewport.outputHeight));
  const canvas = createCanvas(targetWidth, targetHeight);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('2D context is not available for motion viewport canvas');
  }

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(
    source,
    viewport.sourceCropX,
    viewport.sourceCropY,
    viewport.sourceCropWidth,
    viewport.sourceCropHeight,
    0,
    0,
    targetWidth,
    targetHeight
  );
  return canvas;
}

async function loadMotionTracking(url: string, template: MockupTemplate): Promise<MotionTrackingSequence> {
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Motion tracking request failed: ${response.status}`);
  }

  const payload = (await response.json()) as unknown;
  if (!payload || typeof payload !== 'object') {
    throw new Error('Motion tracking JSON is invalid.');
  }

  const value = payload as Record<string, unknown>;
  const outputWidth = positiveNumber(value.outputWidth) ?? template.outputSize ?? DEFAULT_OUTPUT_SIZE;
  const outputHeight = positiveNumber(value.outputHeight) ?? template.outputSize ?? DEFAULT_OUTPUT_SIZE;
  const fps = positiveNumber(value.fps) ?? template.fps ?? DEFAULT_FPS;
  const rawFrames = Array.isArray(value.frames) ? value.frames : [];
  const frames = rawFrames
    .map((entry, fallbackIndex) => normalizeTrackingFrame(entry, fallbackIndex, outputWidth, outputHeight, fps))
    .filter((frame): frame is MotionTrackingFrame => frame !== null);

  if (frames.length === 0) {
    throw new Error('Motion tracking JSON does not contain valid frames.');
  }

  const frameMap = new Map<number, MotionTrackingFrame>();
  for (const frame of frames) {
    frameMap.set(frame.index, frame);
  }

  const bounds = computeTrackingBounds(frames);

  return {
    fps,
    frameCount: positiveInteger(value.frameCount) ?? frames.length,
    duration: positiveNumber(value.duration) ?? frames.length / fps,
    outputWidth,
    outputHeight,
    frames,
    frameMap,
    bounds,
    staticQuad: frames.every((frame) => isSameQuad(frame.screenQuad, frames[0].screenQuad))
  };
}

function normalizeTrackingFrame(
  entry: unknown,
  fallbackIndex: number,
  outputWidth: number,
  outputHeight: number,
  fps: number
): MotionTrackingFrame | null {
  if (!entry || typeof entry !== 'object') {
    return null;
  }

  const value = entry as Record<string, unknown>;
  const screenQuad =
    parseQuad(value.screenQuad) ??
    scaleNormalizedQuad(parseQuad(value.screenQuadNormalized), outputWidth, outputHeight);

  if (!screenQuad) {
    return null;
  }

  return {
    index: positiveInteger(value.index) ?? fallbackIndex,
    time: finiteNumber(value.time) ?? fallbackIndex / fps,
    screenQuad
  };
}

function parseQuad(value: unknown): Point[] | null {
  if (!Array.isArray(value) || value.length !== 4) {
    return null;
  }

  const points = value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }
      const point = entry as Record<string, unknown>;
      const x = Number(point.x);
      const y = Number(point.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        return null;
      }
      return { x, y };
    })
    .filter((point): point is Point => point !== null);

  return points.length === 4 ? points : null;
}

function scaleNormalizedQuad(points: Point[] | null, outputWidth: number, outputHeight: number): Point[] | null {
  if (!points) {
    return null;
  }

  return points.map((point) => ({
    x: point.x * outputWidth,
    y: point.y * outputHeight
  }));
}

function resolveTrackingFrame(sequence: MotionTrackingSequence, frameIndex: number): MotionTrackingFrame {
  const clampedIndex = Math.max(0, Math.min(frameIndex + TRACKING_FRAME_OFFSET, sequence.frameCount - 1));
  const exact = sequence.frameMap.get(clampedIndex);
  if (exact) {
    return exact;
  }

  const byPosition = sequence.frames[Math.min(clampedIndex, sequence.frames.length - 1)];
  if (byPosition) {
    return byPosition;
  }

  return sequence.frames[0];
}

function scaleTrackingQuad(frameQuad: Point[], tracking: MotionTrackingSequence, targetSize: number): Point[] {
  if (tracking.outputWidth === targetSize && tracking.outputHeight === targetSize) {
    return frameQuad;
  }

  const scaleX = targetSize / Math.max(1, tracking.outputWidth);
  const scaleY = targetSize / Math.max(1, tracking.outputHeight);

  return frameQuad.map((point) => ({
    x: point.x * scaleX,
    y: point.y * scaleY
  }));
}

// Module-level cache keyed by (VideoFrame.timestamp, atlasSpec object reference, blur flag).
// This avoids re-extracting identical tile canvases when the atlas frame hasn't changed
// between consecutive render calls (e.g. 30 fps export with a 24 fps atlas video).
let _atlasTileCache: {
  timestamp: number;
  spec: MotionAtlasSpec;
  layout: MotionAtlasLayout;
  templateId: string;
  screenBlurEnabled: boolean;
  alternateScreenMaskEnabled: boolean;
  atlasContract: MotionAtlasContract;
  assets: LoadedMockupAssets;
} | null = null;

function extractAtlasFrameAssets(
  template: MockupTemplate,
  atlasVideo: CanvasImageSource,
  atlasSpec: MotionAtlasSpec,
  layout: MotionAtlasLayout
): LoadedMockupAssets {
  const atlasAuxRole = resolveMotionAtlasAuxRole({
    atlasLayout: layout,
    screenBlurEnabled: template.screenBlurEnabled,
    motionDynamicIslandToggle: template.motionDynamicIslandToggle
  });
  const alternateScreenMaskEnabled = atlasAuxRole === 'alternate-screen-mask';
  const screenBlurEnabled = atlasAuxRole === 'screen-blur-mask';
  const atlasContract = resolveMotionAtlasContract(template);
  // VideoFrame objects carry a precise timestamp we can use as a cache key.
  const cacheTimestamp =
    typeof VideoFrame !== 'undefined' && atlasVideo instanceof VideoFrame
      ? atlasVideo.timestamp
      : -1;

  if (
    _atlasTileCache !== null &&
    cacheTimestamp >= 0 &&
    _atlasTileCache.timestamp === cacheTimestamp &&
    _atlasTileCache.templateId === template.id &&
    _atlasTileCache.spec === atlasSpec &&
    _atlasTileCache.screenBlurEnabled === screenBlurEnabled &&
    _atlasTileCache.alternateScreenMaskEnabled === alternateScreenMaskEnabled &&
    _atlasTileCache.atlasContract === atlasContract
  ) {
    return _atlasTileCache.assets;
  }

  if (
    _atlasTileCache !== null &&
    hasSameAtlasCanvasShape(
      _atlasTileCache,
      atlasSpec,
      layout,
      screenBlurEnabled,
      alternateScreenMaskEnabled,
      atlasContract
    )
  ) {
    const assets = _atlasTileCache.assets;
    assets.template = template;
    redrawAtlasTile(assets.base, atlasVideo, atlasSpec, 0, 0);
    redrawAtlasTile(assets.mask, atlasVideo, atlasSpec, 1, 0);
    if (assets.mask2) redrawAtlasTile(assets.mask2, atlasVideo, atlasSpec, 0, 1);
    if (assets.screenBlurMask) redrawAtlasTile(assets.screenBlurMask, atlasVideo, atlasSpec, 0, 1);
    if (atlasContract === 'screen-overlay-v1') redrawAtlasTile(assets.overlay, atlasVideo, atlasSpec, 1, 1);
    if (assets.backgroundMask) redrawAtlasTile(assets.backgroundMask, atlasVideo, atlasSpec, 2, 0);
    if (assets.reflectionMask) redrawAtlasTile(assets.reflectionMask, atlasVideo, atlasSpec, 1, 1);
    if (assets.reflectionBlurMask) redrawAtlasTile(assets.reflectionBlurMask, atlasVideo, atlasSpec, 2, 1);

    _atlasTileCache.timestamp = cacheTimestamp;
    _atlasTileCache.spec = atlasSpec;
    _atlasTileCache.templateId = template.id;
    return assets;
  }

  const base = extractAtlasTile(atlasVideo, atlasSpec, 0, 0);
  const mask = extractAtlasTile(atlasVideo, atlasSpec, 1, 0);
  // standard-4: (0,1) is either blur, an explicit alternate screen mask, or ignored.
  // extended-6: (0,1) remains the legacy alternate screen mask.
  const mask2 = alternateScreenMaskEnabled ? extractAtlasTile(atlasVideo, atlasSpec, 0, 1) : null;
  const screenBlurMask = screenBlurEnabled ? extractAtlasTile(atlasVideo, atlasSpec, 0, 1) : null;
  const overlay =
    atlasContract === 'screen-overlay-v1'
      ? extractAtlasTile(atlasVideo, atlasSpec, 1, 1)
      : createTransparentTile(atlasSpec.tileWidth, atlasSpec.tileHeight);
  const backgroundMask = layout === 'extended-6' ? extractAtlasTile(atlasVideo, atlasSpec, 2, 0) : null;
  const reflectionMask =
    atlasContract === 'legacy-reflection-v1' ? extractAtlasTile(atlasVideo, atlasSpec, 1, 1) : null;
  const reflectionBlurMask =
    atlasContract === 'legacy-reflection-v1' && layout === 'extended-6'
      ? extractAtlasTile(atlasVideo, atlasSpec, 2, 1)
      : null;

  const assets: LoadedMockupAssets = {
    template,
    base,
    mask,
    mask2,
    overlay,
    backgroundMask,
    reflectionMask,
    reflectionBlurMask,
    screenBlurMask
  };

  _atlasTileCache = {
    timestamp: cacheTimestamp,
    spec: atlasSpec,
    layout,
    templateId: template.id,
    screenBlurEnabled,
    alternateScreenMaskEnabled,
    atlasContract,
    assets
  };

  return assets;
}

function extractAtlasTile(
  atlasVideo: CanvasImageSource,
  atlasSpec: MotionAtlasSpec,
  column: number,
  row: number
): HTMLCanvasElement {
  const canvas = createCanvas(atlasSpec.tileWidth, atlasSpec.tileHeight);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context unavailable for motion atlas tile.');
  }

  ctx.drawImage(
    atlasVideo,
    column * atlasSpec.tileWidth,
    row * atlasSpec.tileHeight,
    atlasSpec.tileWidth,
    atlasSpec.tileHeight,
    0,
    0,
    atlasSpec.tileWidth,
    atlasSpec.tileHeight
  );

  return canvas;
}

function hasSameAtlasCanvasShape(
  cache: NonNullable<typeof _atlasTileCache>,
  spec: MotionAtlasSpec,
  layout: MotionAtlasLayout,
  screenBlurEnabled: boolean,
  alternateScreenMaskEnabled: boolean,
  atlasContract: MotionAtlasContract
): boolean {
  return (
    cache.spec.tileWidth === spec.tileWidth &&
    cache.spec.tileHeight === spec.tileHeight &&
    cache.layout === layout &&
    cache.screenBlurEnabled === screenBlurEnabled &&
    cache.alternateScreenMaskEnabled === alternateScreenMaskEnabled &&
    cache.atlasContract === atlasContract
  );
}

function redrawAtlasTile(
  target: CanvasImageSource,
  atlasVideo: CanvasImageSource,
  atlasSpec: MotionAtlasSpec,
  column: number,
  row: number
): void {
  if (!(target instanceof HTMLCanvasElement)) {
    throw new Error('Cached motion atlas tile is not a canvas.');
  }
  const ctx = target.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas context unavailable for cached motion atlas tile.');
  }
  ctx.clearRect(0, 0, target.width, target.height);
  ctx.drawImage(
    atlasVideo,
    column * atlasSpec.tileWidth,
    row * atlasSpec.tileHeight,
    atlasSpec.tileWidth,
    atlasSpec.tileHeight,
    0,
    0,
    atlasSpec.tileWidth,
    atlasSpec.tileHeight
  );
}

function createTransparentTile(width: number, height: number): HTMLCanvasElement {
  return createCanvas(width, height);
}

function computeTrackingBounds(frames: MotionTrackingFrame[]): MotionTrackingSequence['bounds'] {
  let minX = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const frame of frames) {
    for (const point of frame.screenQuad) {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    }
  }

  return {
    minX: Number.isFinite(minX) ? minX : 0,
    maxX: Number.isFinite(maxX) ? maxX : 0,
    minY: Number.isFinite(minY) ? minY : 0,
    maxY: Number.isFinite(maxY) ? maxY : 0
  };
}

function isSameQuad(a: Point[], b: Point[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((point, index) => {
    const other = b[index];
    return Math.abs(point.x - other.x) < 1e-6 && Math.abs(point.y - other.y) < 1e-6;
  });
}

function reduceRealtimeReflection(reflection?: ReflectionAdjustments): ReflectionAdjustments | undefined {
  if (!reflection) {
    return undefined;
  }

  return {
    ...reflection,
    // Playback preview should stay responsive on weaker devices.
    // Keep reflection visible, but remove blur work entirely in realtime mode.
    blur: 0
  };
}

function positiveNumber(value: unknown): number | null {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
}

function positiveInteger(value: unknown): number | null {
  const parsed = Number(value);
  if (!Number.isInteger(parsed) || parsed < 0) {
    return null;
  }
  return parsed;
}

function finiteNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
